import { useRef, useState } from "react";

/** Qué tanto hay que jalar para que cambie de caso (fracción del ancho). */
const UMBRAL = 0.22;
/** Tope de lo que se mueve el contenido mientras jalas. */
const TOPE_PX = 120;
/** Resistencia: el contenido sigue al dedo, pero a menos de la mitad. */
const FRICCION = 0.45;
/** Con más resistencia todavía cuando no hay caso de ese lado (rebote). */
const FRICCION_TOPE = 0.14;
/**
 * Franja del borde izquierdo que NO se toca: ahí vive el «regresar» del sistema
 * en iOS y Android. Robárselo hace que la app se sienta descompuesta.
 */
const BORDE_SISTEMA_PX = 24;
/** Un gesto cuenta como horizontal si avanza esto más en X que en Y. */
const RATIO_HORIZONTAL = 1.4;
/** Lo que tarda el contenido en regresar a su lugar cuando el gesto no alcanzó. */
const MS_REGRESO = 190;
const CURVA = "cubic-bezier(.32,.72,0,1)";

/**
 * ⚠️ Mientras el dedo arrastra, la animación es SOLO `transform`. Nada de
 * opacidad.
 *
 * El contenedor del caso mide ~4000 px de alto y trae ~350 nodos (el
 * encabezado más toda la bitácora). Animarle la opacidad obliga al teléfono a
 * mezclar esa textura completa en CADA cuadro, y ahí es donde se sentía que se
 * trababa. Con `transform` a secas el compositor solo mueve la capa.
 *
 * El CAMBIO de caso ya no lo anima este hook: lo hace el navegador con View
 * Transitions (ver `lib/transicionCaso.ts`). Aquí solo vive el arrastre.
 */

/** El gesto se descarta si empezó donde ya significa otra cosa. */
function zonaProhibida(destino: EventTarget | null): boolean {
  if (!(destino instanceof Element)) return false;
  // Escribiendo o seleccionando texto.
  if (destino.closest("input, textarea, select, [contenteditable='true']")) return true;
  // Un modal o el visor de evidencias abiertos mandan sobre la página.
  if (document.querySelector('[role="dialog"], [data-visor-archivo]')) return true;
  // Algo que ya se desplaza de lado (tablas anchas, tiras de chips): ese
  // scroll es suyo, no nuestro.
  let n: Element | null = destino;
  while (n && n !== document.body) {
    if (n.scrollWidth > n.clientWidth + 4) {
      const ox = getComputedStyle(n).overflowX;
      if (ox === "auto" || ox === "scroll") return true;
    }
    n = n.parentElement;
  }
  return false;
}

export type SwipeCasos = {
  /** Se esparcen sobre el contenedor del contenido. */
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    onTouchCancel: () => void;
  };
  /** Píxeles que se corrió el contenido (negativo = jalando a la izquierda). */
  desplazamiento: number;
  /** Si el dedo sigue abajo (para quitar la transición y que no se sienta lento). */
  arrastrando: boolean;
  /** Hacia dónde iría si sueltas ahora. null = se regresa a su lugar. */
  destino: "prev" | "next" | null;
  /** Se pega tal cual en el `style` del contenedor. */
  estilo: React.CSSProperties;
};

/**
 * Pasar de caso jalando con el dedo, como en una galería de fotos.
 *
 * Se monta sobre el MISMO recorrido que las flechas «Anterior/Siguiente» y el
 * atajo Ctrl+←/→ (la lista con tus filtros y tu orden), y los tres caminos
 * terminan en la misma transición.
 *
 * El contenido sigue al dedo con resistencia, y si de ese lado no hay caso
 * rebota en vez de moverse: se siente el tope en lugar de soltar en el vacío.
 *
 * NO usa `touch-action: pan-y` a propósito. Esa sería la forma "de libro" de
 * quedarse el gesto horizontal, pero se hereda a los hijos y rompería el scroll
 * de lado de las tablas anchas. En vez de eso el gesto se descarta en JS cuando
 * empieza sobre algo que ya se desplaza solo (ver `zonaProhibida`).
 */
export function useSwipeCasos(opts: {
  hayPrev: boolean;
  hayNext: boolean;
  /** Reciben el punto donde quedó el contenido, para que el carrusel siga de ahí. */
  irPrev: (desde: number) => void;
  irNext: (desde: number) => void;
  activo?: boolean;
}): SwipeCasos {
  const { hayPrev, hayNext, irPrev, irNext, activo = true } = opts;
  const inicio = useRef<{ x: number; y: number } | null>(null);
  const eje = useRef<"?" | "x" | "y">("?");
  // El recorrido REAL del dedo vive en una referencia, no en el estado: al
  // soltar hay que decidir con el último valor, y el estado de React puede ir
  // un render atrás (los eventos táctiles se agrupan). Con el estado se perdía
  // el gesto cuando llegaban juntos.
  const dxRef = useRef(0);
  const [desplazamiento, setDesplazamiento] = useState(0);
  // "soltando" = el carrusel ya tomó el relevo: el contenido vuelve a cero de
  // golpe (sin transición) para que la foto del «antes» salga derecha, y el
  // deslizamiento continúa desde donde iba el dedo.
  const [modo, setModo] = useState<"quieto" | "arrastrando" | "soltando">("quieto");

  function limpiarGesto() {
    inicio.current = null;
    eje.current = "?";
    dxRef.current = 0;
  }

  /** Vuelve a su lugar con transición (el gesto no alcanzó, o lo cancelaron). */
  function regresar() {
    limpiarGesto();
    setModo("quieto");
    setDesplazamiento(0);
  }

  function onTouchStart(e: React.TouchEvent) {
    if (!activo || e.touches.length !== 1) return;
    const t = e.touches[0];
    if (t.clientX < BORDE_SISTEMA_PX) return;
    if (zonaProhibida(e.target)) return;
    inicio.current = { x: t.clientX, y: t.clientY };
    eje.current = "?";
  }

  function onTouchMove(e: React.TouchEvent) {
    const ini = inicio.current;
    if (!ini || e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - ini.x;
    const dy = t.clientY - ini.y;

    // Se decide UNA vez si el gesto es horizontal o vertical. Sin esto, un
    // scroll con la muñeca chueca movía el contenido de lado a media lectura.
    if (eje.current === "?") {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      eje.current = Math.abs(dx) > Math.abs(dy) * RATIO_HORIZONTAL ? "x" : "y";
      if (eje.current === "y") {
        inicio.current = null;
        return;
      }
      setModo("arrastrando");
    }

    dxRef.current = dx;
    const haciaPrev = dx > 0;
    const disponible = haciaPrev ? hayPrev : hayNext;
    const friccion = disponible ? FRICCION : FRICCION_TOPE;
    const crudo = dx * friccion;
    setDesplazamiento(Math.max(-TOPE_PX, Math.min(TOPE_PX, crudo)));
  }

  function onTouchEnd() {
    const ini = inicio.current;
    if (!ini || eje.current !== "x") return regresar();
    const ancho = window.innerWidth || 390;
    // Se mide el recorrido REAL del dedo, no lo que se movió el contenido (que
    // va con resistencia y se topa).
    const dx = dxRef.current;
    const paso = Math.abs(dx) > ancho * UMBRAL;
    const hacia = dx > 0 ? 1 : -1;
    const puede = paso && (hacia === 1 ? hayPrev : hayNext);
    // Dónde quedó el contenido: de ahí arranca el carrusel.
    const offset = desplazamiento;

    if (!puede) {
      // No alcanzó (o no hay caso de ese lado): se regresa a su lugar.
      regresar();
      return;
    }

    limpiarGesto();
    // El contenido vuelve a cero SIN transición y en el mismo cuadro en que
    // arranca el carrusel, así que ese cero nunca se alcanza a pintar: la foto
    // del «antes» sale derecha y el deslizamiento continúa desde `offset`.
    setModo("soltando");
    setDesplazamiento(0);
    if (hacia === 1) irPrev(offset);
    else irNext(offset);
    // Si la transición no se pudo hacer (navegador viejo), que el arrastre
    // vuelva a estar disponible en el siguiente cuadro.
    requestAnimationFrame(() => setModo("quieto"));
  }

  /**
   * ⚠️ `touchcancel` NO es opcional. Android lo manda cuando el sistema se
   * queda el gesto (gesto de navegación, multitouch, el navegador decide hacer
   * pan). Sin este handler `onTouchEnd` no llega nunca y el contenido se queda
   * chueco, con la transición apagada, hasta el siguiente gesto.
   */
  function onTouchCancel() {
    regresar();
  }

  const ancho = typeof window !== "undefined" ? window.innerWidth || 390 : 390;
  const listo = Math.abs(dxRef.current) > ancho * UMBRAL;
  const destino: "prev" | "next" | null = !listo
    ? null
    : desplazamiento > 0
      ? hayPrev
        ? "prev"
        : null
      : hayNext
        ? "next"
        : null;

  const arrastrando = modo === "arrastrando";
  const transicion =
    modo === "quieto" ? `transform ${MS_REGRESO}ms ${CURVA}` : "none";

  return {
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    desplazamiento,
    arrastrando,
    destino,
    estilo: {
      transform: desplazamiento ? `translate3d(${desplazamiento}px,0,0)` : undefined,
      transition: transicion,
      // Solo `transform`: prometerle también `opacity` haría que el navegador
      // prepare la capa para mezclar, que es lo que se quiere evitar.
      willChange: modo === "quieto" ? undefined : "transform",
    } as React.CSSProperties,
  };
}
